import { TaskDefinition } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { EntityAPIResponse } from 'src/@types/SNOWDataServiceTypes';
import { SubWorkflowTaskGroup } from '../subWorkflowRequirementUtils';

const UUID_REGEX =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isTaskIdKey = (key: string) => UUID_REGEX.test(key);

export type EntityFormValues = {
	EntityData: Record<string, unknown>;
	[taskId: string]: Record<string, unknown>;
};

/**
 * Reshape an entity API response into the form-values shape used by RequirementsPortal:
 * - Common attributes from EntityData/EntityAttributes are flattened under EntityData.
 * - Task-bucketed values (UUID-keyed) are mapped to top-level taskId keys, but only
 *   for attributeIds declared in the matching task definition.
 *
 * Returns an empty object when there is nothing to autopopulate so callers can use a
 * truthy/key-count check to skip the merge step.
 */
export const extractEntityValuesForAutopopulation = (
	entityResponse: EntityAPIResponse<Record<string, unknown>> | null | undefined,
	taskDefinitions: TaskDefinition[],
	subWorkflowGroups: SubWorkflowTaskGroup[] = []
): EntityFormValues | Record<string, never> => {
	const entity = entityResponse?.entities?.[0];

	if (!entity || !taskDefinitions.length) {
		return {};
	}

	const entityData = (entity.EntityData ?? {}) as Record<string, unknown>;
	const entityAttributes = (entity.EntityAttributes ?? {}) as Record<
		string,
		unknown
	>;

	const result: EntityFormValues = { EntityData: {} };

	for (const key of Object.keys(entityData)) {
		if (!isTaskIdKey(key)) {
			result.EntityData[key] = entityData[key];
		}
	}

	for (const key of Object.keys(entityAttributes)) {
		if (!isTaskIdKey(key)) {
			result.EntityData[key] = entityAttributes[key];
		}
	}

	const collectTaskValues = (
		tasks: TaskDefinition[],
		taskData: Record<string, unknown>,
		taskAttributes: Record<string, unknown>
	) => {
		for (const taskDef of tasks) {
			const taskId = taskDef.taskId;
			if (!taskId) continue;

			const fromEntityData = (taskData[taskId] ?? {}) as Record<string, unknown>;
			const fromEntityAttributes = (taskAttributes[taskId] ?? {}) as Record<
				string,
				unknown
			>;

			const hasTaskData =
				Object.keys(fromEntityData).length > 0 ||
				Object.keys(fromEntityAttributes).length > 0;

			if (!hasTaskData) continue;

			const taskValues: Record<string, unknown> = {};

			for (const taskInput of taskDef.taskInputs ?? []) {
				const attributeId = taskInput.attributeId;
				if (!attributeId) continue;

				const value =
					fromEntityData[attributeId] ?? fromEntityAttributes[attributeId];
				if (value !== undefined) {
					taskValues[attributeId] = value;
				}
			}

			if (Object.keys(taskValues).length > 0) {
				result[taskId] = taskValues;
			}
		}
	};

	collectTaskValues(taskDefinitions, entityData, entityAttributes);

	// A sub-workflow's task values sit under its workflow id on the parent's row.
	for (const { workflowId, taskDefinitions: subTasks } of subWorkflowGroups) {
		collectTaskValues(
			subTasks,
			(entityData[workflowId] ?? {}) as Record<string, unknown>,
			(entityAttributes[workflowId] ?? {}) as Record<string, unknown>
		);
	}

	return result;
};

/**
 * Merge autopopulation values onto a base requirements object, preserving base keys
 * the entity didn't supply. Returns a new object — does not mutate inputs.
 */
export const mergeRequirementsWithEntityValues = (
	baseRequirements: Record<string, any>,
	entityValues: EntityFormValues | Record<string, never>
): Record<string, any> => {
	if (!entityValues || Object.keys(entityValues).length === 0) {
		return baseRequirements;
	}

	const taskValuesMerge = Object.keys(entityValues)
		.filter((key) => key !== 'EntityData')
		.reduce<Record<string, Record<string, unknown>>>((acc, taskId) => {
			acc[taskId] = {
				...(baseRequirements[taskId] ?? {}),
				...(entityValues as EntityFormValues)[taskId],
			};
			return acc;
		}, {});

	return {
		...baseRequirements,
		EntityData: {
			...(baseRequirements.EntityData ?? {}),
			...((entityValues as EntityFormValues).EntityData ?? {}),
		},
		...taskValuesMerge,
	};
};
